import React, { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Sparkles, Users, Calendar, Music, Utensils, ChevronDown, ChevronUp } from "lucide-react";

const PrivateEvents = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    event_type: "",
    preferred_date: "",
    guest_count: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.event_type || !formData.preferred_date || !formData.guest_count) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/private-events/inquiry`, {
        ...formData,
        guest_count: parseInt(formData.guest_count)
      });
      setSubmitted(true);
      toast.success("Thank you! We'll be in touch soon.");
    } catch (error) {
      toast.error("Failed to submit inquiry. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-obsidian pt-20 flex items-center justify-center px-4">
        <div className="text-center max-w-lg">
          <Sparkles className="w-16 h-16 text-electric mx-auto mb-6" />
          <h1 className="font-display text-3xl text-white mb-4">
            Thank You!
          </h1>
          <p className="text-white/60 mb-8">
            Your private event inquiry has been received. Our events team will contact you 
            within 24-48 hours to discuss your vision and availability.
          </p>
          <Button 
            onClick={() => setSubmitted(false)}
            variant="outline"
            className="border-electric text-electric hover:bg-electric/10"
          >
            Submit Another Inquiry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian pt-20" data-testid="private-events-page">
      {/* Hero */}
      <section 
        className="relative py-24"
        style={{
          backgroundImage: `url('/assets/hero-sax.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-obsidian/80"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-electric font-display italic mb-2">Strengthening Our Community</p>
          <h1 className="font-display text-4xl sm:text-5xl text-white mb-4">
            Private Events & Rentals
          </h1>
          <p className="text-white/60 max-w-2xl">
            The Brownstone Jazz Club is available to rent for private parties or special events. 
            Our beautiful and welcoming space is perfect for those looking to host their next event 
            requiring a playful yet elegant setting. Contact us today to schedule a tour.
          </p>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-8 border border-white/10 rounded-lg bg-obsidian-light">
              <Users className="w-10 h-10 text-electric mx-auto mb-4" />
              <h3 className="font-display text-xl text-white mb-2">Capacity</h3>
              <p className="text-white/50">Up to 120 guests for cocktail style events, 80 for seated arrangements</p>
            </div>
            <div className="text-center p-8 border border-white/10 rounded-lg bg-obsidian-light">
              <Music className="w-10 h-10 text-electric mx-auto mb-4" />
              <h3 className="font-display text-xl text-white mb-2">Live Entertainment</h3>
              <p className="text-white/50">Our house band or your own performers on our professional stage</p>
            </div>
            <div className="text-center p-8 border border-white/10 rounded-lg bg-obsidian-light">
              <Utensils className="w-10 h-10 text-electric mx-auto mb-4" />
              <h3 className="font-display text-xl text-white mb-2">Catering Available</h3>
              <p className="text-white/50">Bar service & catering available upon request through our trusted local partners</p>
            </div>
          </div>
        </div>
      </section>

      {/* Inquiry Form */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Form */}
            <div>
              <h2 className="font-display text-3xl text-white mb-6">
                Request a Quote
              </h2>
              <p className="text-white/60 mb-8">
                Fill out the form below and our events team will contact you within 24-48 hours 
                to schedule a tour and discuss your event needs.
              </p>

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="private-event-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-white/70">Name *</Label>
                    <Input 
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="bg-obsidian border-white/20 text-white focus:border-electric"
                      data-testid="private-event-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-white/70">Email *</Label>
                    <Input 
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      className="bg-obsidian border-white/20 text-white focus:border-electric"
                      data-testid="private-event-email"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-white/70">Phone</Label>
                  <Input 
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    className="bg-obsidian border-white/20 text-white focus:border-electric"
                    data-testid="private-event-phone"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="event_type" className="text-white/70">Event Type *</Label>
                  <Select 
                    value={formData.event_type}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, event_type: value }))}
                  >
                    <SelectTrigger className="bg-obsidian border-white/20 text-white" data-testid="private-event-type">
                      <SelectValue placeholder="Select event type" />
                    </SelectTrigger>
                    <SelectContent className="bg-obsidian-light border-white/10">
                      <SelectItem value="birthday">Birthday Party</SelectItem>
                      <SelectItem value="corporate">Corporate Event</SelectItem>
                      <SelectItem value="wedding">Wedding Reception</SelectItem>
                      <SelectItem value="anniversary">Anniversary</SelectItem>
                      <SelectItem value="fundraiser">Fundraiser</SelectItem>
                      <SelectItem value="album_release">Album Release</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="preferred_date" className="text-white/70">Preferred Date *</Label>
                    <Input 
                      id="preferred_date"
                      type="date"
                      value={formData.preferred_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, preferred_date: e.target.value }))}
                      className="bg-obsidian border-white/20 text-white focus:border-electric"
                      data-testid="private-event-date"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="guest_count" className="text-white/70">Guest Count *</Label>
                    <Input 
                      id="guest_count"
                      type="number"
                      min="1"
                      max="150"
                      value={formData.guest_count}
                      onChange={(e) => setFormData(prev => ({ ...prev, guest_count: e.target.value }))}
                      className="bg-obsidian border-white/20 text-white focus:border-electric"
                      data-testid="private-event-guests"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-white/70">Tell us about your event</Label>
                  <Textarea 
                    id="message"
                    value={formData.message}
                    onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                    rows={4}
                    className="bg-obsidian border-white/20 text-white focus:border-electric resize-none"
                    placeholder="Share your vision, special requests, or any questions..."
                    data-testid="private-event-message"
                  />
                </div>

                <Button 
                  type="submit"
                  disabled={loading}
                  className="w-full bg-electric text-obsidian hover:bg-electric-light py-6"
                  data-testid="private-event-submit"
                >
                  {loading ? (
                    <span className="w-5 h-5 border-2 border-obsidian border-t-transparent rounded-full animate-spin"></span>
                  ) : (
                    "Submit Inquiry"
                  )}
                </Button>
              </form>
            </div>

            {/* Image Gallery */}
            <div className="space-y-6">
              <img 
                src="/assets/exterior.jpg"
                alt="BrownStone Jazz Club exterior"
                className="rounded-lg w-full"
              />
              <div className="grid grid-cols-2 gap-4">
                <img 
                  src="/assets/performance-1.jpg"
                  alt="Live performance"
                  className="rounded-lg w-full aspect-square object-cover"
                />
                <img 
                  src="/assets/treble-sculpture.jpg"
                  alt="Treble clef sculpture"
                  className="rounded-lg w-full aspect-square object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQSection />
    </div>
  );
};

// FAQ Component
const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);
  
  const faqs = [
    {
      question: "Does the jazz club serve food and drinks?",
      answer: "The BrownStone Jazz Club is a music venue focused on live performances. We do not have an in house kitchen or bar. However, for private events, we can arrange full bar service and catering through our trusted network of local catering companies and businesses. Just let us know your needs when submitting your inquiry!"
    },
    {
      question: "Can I bring my own food or drinks to a private event?",
      answer: "For private events, we work with local catering partners to provide food and beverage services. If you have specific vendors in mind, we're happy to discuss options. Please mention any preferences in your event inquiry."
    },
    {
      question: "What's included in the venue rental?",
      answer: "Your rental includes use of our beautiful space, professional sound system, stage access, ambient lighting, and seating arrangements. Entertainment, catering, and bar services are available as additions through our partner network."
    },
    {
      question: "Can I bring my own band or DJ?",
      answer: "Absolutely! You can bring your own performers or choose to feature our talented house band. We have a professional stage setup ready for live music."
    },
    {
      question: "What is the capacity of the venue?",
      answer: "We can accommodate up to 120 guests for cocktail style events and approximately 80 guests for seated arrangements. The exact capacity depends on your event layout preferences."
    },
    {
      question: "How far in advance should I book?",
      answer: "We recommend booking at least 4 to 6 weeks in advance for private events, especially for weekend dates. Popular dates like holidays can book up quickly, so earlier is always better!"
    }
  ];

  return (
    <section className="py-16 bg-obsidian-light" data-testid="faq-section">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <p className="text-electric font-display italic mb-2">Questions?</p>
          <h2 className="font-display text-3xl text-white">Frequently Asked Questions</h2>
        </div>
        
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div 
              key={index}
              className="border border-white/10 rounded-lg overflow-hidden bg-obsidian"
              data-testid={`faq-item-${index}`}
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full flex items-center justify-between p-5 text-left hover:bg-white/5 transition-colors"
                data-testid={`faq-toggle-${index}`}
              >
                <span className="font-display text-white pr-4">{faq.question}</span>
                {openIndex === index ? (
                  <ChevronUp className="w-5 h-5 text-electric flex-shrink-0" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-white/50 flex-shrink-0" />
                )}
              </button>
              {openIndex === index && (
                <div className="px-5 pb-5">
                  <p className="text-white/60 leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PrivateEvents;
